// react
import { useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Box,
	Card,
	CardContent,
	Chip,
	Divider,
	Grid,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	Add as IconAdd,
	Assessment as IconReport,
	CalendarMonth as IconCalendar,
	Edit as IconEdit,
	PictureAsPdf as IconPdf,
	Visibility as IconView,
} from '@mui/icons-material';

// components
import { PaginationBar, SearchFilterBar } from '../../../ui/SearchFilterBar/SearchFilterBar';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// ui
import { BtnSplit } from '../../../ui/BtnSplit/BtnSplit';
import { Empty } from '../../../ui/Empty/Empty';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { formatDate } from '../../../../utils/dates';

// Types - Mock Data for Demonstration Reports

interface DemonstrationReport {
	id: string;
	reportNumber: string;
	title: string;
	reportingPeriod: {
		start: string;
		end: string;
	};
	status: 'draft' | 'under_review' | 'submitted' | 'approved' | 'rejected';
	submissionDeadline: string;
	submittedDate?: string;
	totalExpenditure: number;
	currency: string;
	workPlansIncluded: number;
	activitiesIncluded: number;
	documentsAttached: number;
	createdBy: string;
	createdAt: string;
	lastModified: string;
}

// Mock data for demonstration
const mockDemonstrationReports: DemonstrationReport[] = [
	{
		id: 'dr-001',
		reportNumber: 'DR-2026-Q1',
		title: 'Q1 2026 Demonstration Report',
		reportingPeriod: {
			start: '2026-01-01',
			end: '2026-03-31',
		},
		status: 'submitted',
		submissionDeadline: '2026-04-15',
		submittedDate: '2026-04-10',
		totalExpenditure: 2500000,
		currency: 'USD',
		workPlansIncluded: 3,
		activitiesIncluded: 15,
		documentsAttached: 24,
		createdBy: 'John Smith',
		createdAt: '2026-03-25',
		lastModified: '2026-04-10',
	},
	{
		id: 'dr-002',
		reportNumber: 'DR-2025-Q4',
		title: 'Q4 2025 Demonstration Report',
		reportingPeriod: {
			start: '2025-10-01',
			end: '2025-12-31',
		},
		status: 'approved',
		submissionDeadline: '2026-01-15',
		submittedDate: '2026-01-12',
		totalExpenditure: 1850000,
		currency: 'USD',
		workPlansIncluded: 2,
		activitiesIncluded: 12,
		documentsAttached: 18,
		createdBy: 'Jane Doe',
		createdAt: '2025-12-20',
		lastModified: '2026-01-12',
	},
	{
		id: 'dr-003',
		reportNumber: 'DR-2026-Q2',
		title: 'Q2 2026 Demonstration Report',
		reportingPeriod: {
			start: '2026-04-01',
			end: '2026-06-30',
		},
		status: 'draft',
		submissionDeadline: '2026-07-15',
		totalExpenditure: 750000,
		currency: 'USD',
		workPlansIncluded: 2,
		activitiesIncluded: 8,
		documentsAttached: 5,
		createdBy: 'John Smith',
		createdAt: '2026-06-01',
		lastModified: '2026-06-10',
	},
	{
		id: 'dr-004',
		reportNumber: 'DR-2025-Q3',
		title: 'Q3 2025 Demonstration Report',
		reportingPeriod: {
			start: '2025-07-01',
			end: '2025-09-30',
		},
		status: 'approved',
		submissionDeadline: '2025-10-15',
		submittedDate: '2025-10-08',
		totalExpenditure: 1200000,
		currency: 'USD',
		workPlansIncluded: 2,
		activitiesIncluded: 10,
		documentsAttached: 15,
		createdBy: 'Jane Doe',
		createdAt: '2025-09-15',
		lastModified: '2025-10-08',
	},
];

// Component

export const DemonstrationReports = () => {
	// hooks
	const { t } = useTranslation();
	const theme = useTheme();

	// state
	const [reports] = useState<DemonstrationReport[]>(mockDemonstrationReports);
	const [searchQuery, setSearchQuery] = useState('');
	const [filterStatus, setFilterStatus] = useState<string>('ALL');
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	// filtered reports
	const filteredReports = useMemo(() => {
		return reports.filter((report) => {
			// Search filter
			if (searchQuery) {
				const query = searchQuery.toLowerCase();
				const matchesSearch =
					report.title.toLowerCase().includes(query) ||
					report.reportNumber.toLowerCase().includes(query);
				if (!matchesSearch) return false;
			}

			// Status filter
			if (filterStatus !== 'ALL' && report.status !== filterStatus) {
				return false;
			}

			return true;
		});
	}, [reports, searchQuery, filterStatus]);

	// pagination
	const paginatedReports = useMemo(() => {
		const startIndex = (page - 1) * rowsPerPage;
		return filteredReports.slice(startIndex, startIndex + rowsPerPage);
	}, [filteredReports, page, rowsPerPage]);

	// summary stats
	const summaryStats = useMemo(() => {
		const totalReports = reports.length;
		const draftReports = reports.filter((r) => r.status === 'draft').length;
		const submittedReports = reports.filter((r) => r.status === 'submitted').length;
		const approvedReports = reports.filter((r) => r.status === 'approved').length;
		const totalExpenditure = reports
			.filter((r) => r.status === 'approved')
			.reduce((sum, r) => sum + r.totalExpenditure, 0);

		return { totalReports, draftReports, submittedReports, approvedReports, totalExpenditure };
	}, [reports]);

	// helpers
	const getStatusColor = (status: DemonstrationReport['status']) => {
		switch (status) {
			case 'draft':
				return 'default';
			case 'under_review':
				return 'info';
			case 'submitted':
				return 'warning';
			case 'approved':
				return 'success';
			case 'rejected':
				return 'error';
			default:
				return 'default';
		}
	};

	const getStatusLabel = (status: DemonstrationReport['status']) => {
		switch (status) {
			case 'draft':
				return t('spendPlan.demonstrationReports.status.draft');
			case 'under_review':
				return t('spendPlan.demonstrationReports.status.underReview');
			case 'submitted':
				return t('spendPlan.demonstrationReports.status.submitted');
			case 'approved':
				return t('spendPlan.demonstrationReports.status.approved');
			case 'rejected':
				return t('spendPlan.demonstrationReports.status.rejected');
			default:
				return status;
		}
	};

	const formatPeriod = (start: string, end: string) => {
		const startDate = new Date(start);
		const endDate = new Date(end);
		return `${startDate.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' })}`;
	};

	// handlers
	const handleViewReport = (report: DemonstrationReport) => {
		// TODO: Navigate to report detail page
		console.log('View report:', report);
	};

	const handleCreateReport = () => {
		// TODO: Open create report dialog
		console.log('Create new report');
	};

	return (
		<Box>
			{/* Header - Full width, no padding */}
			<HeaderListingPage
				icon={IconReport}
				title={t('spendPlan.demonstrationReports.title')}
				subtitle={t('spendPlan.demonstrationReports.subtitle')}
				variant="financial"
				actions={
					<BtnSplit
						actions={[
							{
								children: t('spendPlan.demonstrationReports.createReport'),
								onClick: handleCreateReport,
								startIcon: <IconAdd />,
								variant: 'contained',
							},
						]}
					/>
				}
			/>

			{/* Content with padding */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* Summary Cards */}
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
							<CardContent>
								<Stack spacing={1}>
									<Typography variant="caption" color="text.secondary">
										{t('spendPlan.demonstrationReports.totalReports')}
									</Typography>
									<Typography variant="h4" fontWeight={600}>
										{summaryStats.totalReports}
									</Typography>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
							<CardContent>
								<Stack spacing={1}>
									<Typography variant="caption" color="text.secondary">
										{t('spendPlan.demonstrationReports.draftReports')}
									</Typography>
									<Typography variant="h4" fontWeight={600} color="text.secondary">
										{summaryStats.draftReports}
									</Typography>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
							<CardContent>
								<Stack spacing={1}>
									<Typography variant="caption" color="text.secondary">
										{t('spendPlan.demonstrationReports.approvedReports')}
									</Typography>
									<Typography variant="h4" fontWeight={600} color="success.main">
										{summaryStats.approvedReports}
									</Typography>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
							<CardContent>
								<Stack spacing={1}>
									<Typography variant="caption" color="text.secondary">
										{t('spendPlan.demonstrationReports.totalApprovedExpenditure')}
									</Typography>
									<Typography variant="h4" fontWeight={600} color="primary.main">
										{formatCurrency({ value: summaryStats.totalExpenditure, currency: 'USD' })}
									</Typography>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
				</Grid>

				{/* Search and Filters */}
				<SearchFilterBar
					searchPlaceholder={t('spendPlan.demonstrationReports.searchPlaceholder')}
					searchValue={searchQuery}
					onSearchChange={setSearchQuery}
					filters={[
						{
							key: 'status',
							label: t('status'),
							options: [
								{ label: t('all'), value: 'ALL' },
								{ label: t('spendPlan.demonstrationReports.status.draft'), value: 'draft' },
								{
									label: t('spendPlan.demonstrationReports.status.underReview'),
									value: 'under_review',
								},
								{ label: t('spendPlan.demonstrationReports.status.submitted'), value: 'submitted' },
								{ label: t('spendPlan.demonstrationReports.status.approved'), value: 'approved' },
								{ label: t('spendPlan.demonstrationReports.status.rejected'), value: 'rejected' },
							],
						},
					]}
					filterValues={{ status: filterStatus }}
					onFilterChange={(key, value) => {
						if (key === 'status') setFilterStatus(String(value));
					}}
					resultsCount={filteredReports.length}
					totalCount={reports.length}
				/>

				{/* Reports Table */}
				{paginatedReports.length > 0 ? (
					<Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>{t('spendPlan.demonstrationReports.reportNumber')}</TableCell>
										<TableCell>{t('spendPlan.demonstrationReports.reportingPeriod')}</TableCell>
										<TableCell>{t('status')}</TableCell>
										<TableCell align="right">
											{t('spendPlan.demonstrationReports.expenditure')}
										</TableCell>
										<TableCell align="center">
											{t('spendPlan.demonstrationReports.workPlans')}
										</TableCell>
										<TableCell align="center">
											{t('spendPlan.demonstrationReports.activities')}
										</TableCell>
										<TableCell>{t('spendPlan.demonstrationReports.deadline')}</TableCell>
										<TableCell align="right">{t('actions')}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{paginatedReports.map((report) => (
										<TableRow
											key={report.id}
											hover
											sx={{ cursor: 'pointer' }}
											onClick={() => handleViewReport(report)}>
											<TableCell>
												<Stack spacing={0.5}>
													<Typography variant="body2" fontWeight={600}>
														{report.reportNumber}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{report.title}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Stack direction="row" alignItems="center" spacing={1}>
													<IconCalendar sx={{ fontSize: 16, color: 'text.secondary' }} />
													<Typography variant="body2">
														{formatPeriod(report.reportingPeriod.start, report.reportingPeriod.end)}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Chip
													label={getStatusLabel(report.status)}
													color={getStatusColor(report.status)}
													size="small"
												/>
											</TableCell>
											<TableCell align="right">
												<Typography variant="body2" fontWeight={500}>
													{formatCurrency({
														value: report.totalExpenditure,
														currency: report.currency,
													})}
												</Typography>
											</TableCell>
											<TableCell align="center">
												<Typography variant="body2">{report.workPlansIncluded}</Typography>
											</TableCell>
											<TableCell align="center">
												<Typography variant="body2">{report.activitiesIncluded}</Typography>
											</TableCell>
											<TableCell>
												<Stack spacing={0.5}>
													<Typography variant="body2">
														{formatDate({ date: report.submissionDeadline })}
													</Typography>
													{report.submittedDate && (
														<Typography variant="caption" color="success.main">
															{t('spendPlan.demonstrationReports.submittedOn', {
																date: formatDate({ date: report.submittedDate }),
															})}
														</Typography>
													)}
												</Stack>
											</TableCell>
											<TableCell align="right">
												<Stack direction="row" spacing={0.5} justifyContent="flex-end">
													<Tooltip title={t('view')}>
														<IconButton size="small" onClick={() => handleViewReport(report)}>
															<IconView fontSize="small" />
														</IconButton>
													</Tooltip>
													{report.status === 'draft' && (
														<Tooltip title={t('edit')}>
															<IconButton size="small">
																<IconEdit fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
													{(report.status === 'submitted' || report.status === 'approved') && (
														<Tooltip title={t('spendPlan.demonstrationReports.downloadPdf')}>
															<IconButton size="small">
																<IconPdf fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
												</Stack>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						{/* Pagination */}
						<Divider />
						<Box sx={{ p: 2 }}>
							<PaginationBar
								page={page}
								onPageChange={setPage}
								rowsPerPage={rowsPerPage}
								onRowsPerPageChange={setRowsPerPage}
								totalCount={filteredReports.length}
							/>
						</Box>
					</Paper>
				) : (
					<Empty
						title={{ children: t('spendPlan.demonstrationReports.empty.title') }}
						content={{ children: t('spendPlan.demonstrationReports.empty.description') }}
						IconCircleProps={{ icon: <IconReport sx={{ fontSize: 32 }} /> }}
						actions={[
							{
								children: t('spendPlan.demonstrationReports.createReport'),
								onClick: handleCreateReport,
								startIcon: <IconAdd />,
								variant: 'contained',
							},
						]}
					/>
				)}
			</Box>
		</Box>
	);
};
